import { find, findIndex } from 'lodash';

import { CartLineItem, ISpecial, ISpecialProduct } from '@/types';

/** Result of classifying all BOGO bundle units (active cycle + completed cycles). */
export type BogoClassifyState = {
  activeBuyUnits: number;
  activeGetUnits: number;
  completedDealsInCart: number;
  dependentProductsInCart: CartLineItem[];
  independentProductsInCart: CartLineItem[];
};

/** Single cart quantity slice used for per-unit BOGO classification. */
export type BogoClassifyUnit = CartLineItem & {
  __bundleUnitKey: string;
};

export type BogoClassifyParams = {
  bundleLines: CartLineItem[];
  cartProducts: CartLineItem[];
  depProducts: ISpecialProduct[];
  indProducts: ISpecialProduct[];
  independentRequired: number;
  dependentRequired: number;
};

/** Toast variant after a BOGO add/increment. */
export type BogoNotifyEvent = 'added' | 'unlocked' | 'completed' | null;

/** Counter UX after a BOGO add/increment. */
export type BogoAddUx = {
  openCart: boolean;
  toast: BogoNotifyEvent;
};

/** Expands each bundle line into one entry per quantity for unit-level classification. */
const expandBundleUnits = (bundleLines: CartLineItem[]): BogoClassifyUnit[] =>
  bundleLines.flatMap(line =>
    Array.from({ length: line.quantity }, (_, unitIndex) => ({
      ...line,
      __bundleUnitKey: `${line.id}-${
        line.weightVariant?.id ?? 'default'
      }-${unitIndex}`,
    })),
  );

/** Moves `count` units from `src` into `dest`, tagging them with the BUY/GET role flag. */
const moveUnits = (
  src: BogoClassifyUnit[],
  dest: CartLineItem[],
  count: number,
  role: 'independent' | 'dependent',
): void => {
  for (let n = 0; n < count && src.length > 0; n += 1) {
    const unit = src.shift() as BogoClassifyUnit;
    dest.push({
      ...unit,
      independent: role === 'independent',
      dependent: role === 'dependent',
    });
  }
};

/**
 * Classifies all bundle units into BUY/GET roles and counts completed cycles.
 *
 * A "dual-list" unit (one in both the BUY and GET groups of the deal) can fill either
 * side. To match the discount the backend applies, classification works by counts:
 * it computes the maximum number of complete cycles possible and reserves dual units
 * to cover whatever BUY/GET the dedicated pools can't fill. Assigning unit by unit
 * instead would spend a dual unit on a BUY slot and leave a later cycle's GET unfilled.
 * Leftover units form the active (incomplete) cycle shown by the tracker.
 */
const classifyBogoUnits = ({
  bundleLines,
  cartProducts,
  depProducts,
  indProducts,
  independentRequired,
  dependentRequired,
}: BogoClassifyParams): BogoClassifyState => {
  const indReq = Math.max(1, independentRequired);
  const depReq = Math.max(1, dependentRequired);

  // Only units that are actually in the cart count toward progress.
  const units = expandBundleUnits(bundleLines).filter(
    unit => findIndex(cartProducts, p => p._id === unit._id) !== -1,
  );

  // Split units by role flexibility. A unit in neither the BUY nor the GET group of
  // the deal can't contribute to it, so it's ignored.
  const buyOnly: BogoClassifyUnit[] = [];
  const getOnly: BogoClassifyUnit[] = [];
  const dual: BogoClassifyUnit[] = [];

  units.forEach(unit => {
    const inInd = !!find(indProducts, d => d?.id === unit?.id);
    const inDep = !!find(depProducts, d => d?.id === unit?.id);

    if (inInd && inDep) {
      dual.push(unit);
    } else if (inInd) {
      buyOnly.push(unit);
    } else if (inDep) {
      getOnly.push(unit);
    }
  });

  const buyOnlyCount = buyOnly.length;
  const getOnlyCount = getOnly.length;
  const dualCount = dual.length;

  // Max complete cycles: largest k where dual units cover the BUY/GET shortfall
  // left by the dedicated pools — max(0, k·indReq − B) + max(0, k·depReq − G) ≤ D.
  const maxByTotal = Math.floor(
    (buyOnlyCount + getOnlyCount + dualCount) / (indReq + depReq),
  );

  let completedDealsInCart = 0;
  for (let k = maxByTotal; k >= 1; k -= 1) {
    const dualForBuy = Math.max(0, k * indReq - buyOnlyCount);
    const dualForGet = Math.max(0, k * depReq - getOnlyCount);

    if (dualForBuy + dualForGet <= dualCount) {
      completedDealsInCart = k;
      break;
    }
  }

  const independentProductsInCart: CartLineItem[] = [];
  const dependentProductsInCart: CartLineItem[] = [];

  // Assign roles for the completed cycles: dedicated pools first, dual fills gaps.
  const cycleBuyTotal = completedDealsInCart * indReq;
  const cycleGetTotal = completedDealsInCart * depReq;

  moveUnits(
    buyOnly,
    independentProductsInCart,
    Math.min(buyOnlyCount, cycleBuyTotal),
    'independent',
  );
  moveUnits(
    dual,
    independentProductsInCart,
    Math.max(0, cycleBuyTotal - buyOnlyCount),
    'independent',
  );
  moveUnits(
    getOnly,
    dependentProductsInCart,
    Math.min(getOnlyCount, cycleGetTotal),
    'dependent',
  );
  moveUnits(
    dual,
    dependentProductsInCart,
    Math.max(0, cycleGetTotal - getOnlyCount),
    'dependent',
  );

  // Leftovers form the active (incomplete) cycle. It cannot complete (k is maximal),
  // so cap at indReq / depReq. BUY: buy-only then dual; GET: get-only then dual.
  const activeBuyOnly = Math.min(indReq, buyOnly.length);
  moveUnits(buyOnly, independentProductsInCart, activeBuyOnly, 'independent');
  let activeBuyUnits = activeBuyOnly;

  const activeBuyDual = Math.min(indReq - activeBuyUnits, dual.length);
  moveUnits(dual, independentProductsInCart, activeBuyDual, 'independent');
  activeBuyUnits += activeBuyDual;

  const activeGetOnly = Math.min(depReq, getOnly.length);
  moveUnits(getOnly, dependentProductsInCart, activeGetOnly, 'dependent');
  let activeGetUnits = activeGetOnly;

  const activeGetDual = Math.min(depReq - activeGetUnits, dual.length);
  moveUnits(dual, dependentProductsInCart, activeGetDual, 'dependent');
  activeGetUnits += activeGetDual;

  return {
    activeBuyUnits,
    activeGetUnits,
    completedDealsInCart,
    dependentProductsInCart,
    independentProductsInCart,
  };
};

const getBogoNotifyEvent = (
  beforeBuy: number,
  afterBuy: number,
  independentRequired: number,
): BogoNotifyEvent => {
  const indReq = Math.max(1, independentRequired);

  if (afterBuy === indReq && beforeBuy < indReq) {
    return 'unlocked';
  }

  if (afterBuy > beforeBuy && afterBuy < indReq) {
    return 'added';
  }

  return null;
};

const getBogoAddUx = (
  before: BogoClassifyState,
  after: BogoClassifyState,
  isGetAdd: boolean,
  independentRequired: number,
): BogoAddUx => {
  if (after.completedDealsInCart > before.completedDealsInCart) {
    return { openCart: true, toast: 'completed' };
  }

  if (!isGetAdd) {
    return {
      openCart: false,
      toast: getBogoNotifyEvent(
        before.activeBuyUnits,
        after.activeBuyUnits,
        independentRequired,
      ),
    };
  }

  if (after.activeGetUnits > before.activeGetUnits) {
    return { openCart: false, toast: 'added' };
  }

  return { openCart: false, toast: null };
};

const classifyAfterAddLine = (
  params: BogoClassifyParams,
  line: CartLineItem,
): BogoClassifyState => {
  const qtyToAdd = line.quantity || 1;
  const lineInBundle = params.bundleLines.some(l => l.id === line.id);

  const afterBundleLines = lineInBundle
    ? params.bundleLines.map(l =>
        l.id === line.id ? { ...l, quantity: l.quantity + qtyToAdd } : l,
      )
    : [...params.bundleLines, { ...line, quantity: qtyToAdd }];

  const lineInCart = params.cartProducts.some(p => p.id === line.id);
  const afterCartProducts = lineInCart
    ? params.cartProducts.map(p =>
        p.id === line.id ? { ...p, quantity: p.quantity + qtyToAdd } : p,
      )
    : [...params.cartProducts, { ...line, quantity: qtyToAdd }];

  return classifyBogoUnits({
    ...params,
    bundleLines: afterBundleLines,
    cartProducts: afterCartProducts,
  });
};

const classifyAfterIncrement = (
  params: BogoClassifyParams,
  lineId: string,
): BogoClassifyState => {
  const afterLines = params.bundleLines.map(line =>
    line.id === lineId ? { ...line, quantity: line.quantity + 1 } : line,
  );

  return classifyBogoUnits({
    ...params,
    bundleLines: afterLines,
    cartProducts: params.cartProducts.map(line =>
      line.id === lineId ? { ...line, quantity: line.quantity + 1 } : line,
    ),
  });
};

/** Builds classify params from deal special and cart lines. */
const buildBogoClassifyParams = (
  special: ISpecial,
  bundleLines: CartLineItem[],
  cartProducts: CartLineItem[],
): BogoClassifyParams => ({
  bundleLines,
  cartProducts,
  depProducts: special.depProducts,
  indProducts: special.indProducts,
  independentRequired: special.independentProductsRequired,
  dependentRequired: special.dependentProductsRequired,
});

/** Resolves toast and cart-open for a new BOGO line add (before add runs). */
const getBogoAddUxForAddLine = (
  params: BogoClassifyParams,
  line: CartLineItem,
  isGetAdd: boolean,
  independentRequired: number,
): BogoAddUx => {
  const before = classifyBogoUnits(params);
  const after = classifyAfterAddLine(params, line);

  return getBogoAddUx(before, after, isGetAdd, independentRequired);
};

/** Resolves toast and cart-open for +1 on an existing BOGO line (before increment runs). */
const getBogoAddUxForIncrement = (
  params: BogoClassifyParams,
  lineId: string,
  isGetAdd: boolean,
  independentRequired: number,
): BogoAddUx => {
  const before = classifyBogoUnits(params);
  const after = classifyAfterIncrement(params, lineId);

  return getBogoAddUx(before, after, isGetAdd, independentRequired);
};

export {
  buildBogoClassifyParams,
  classifyBogoUnits,
  getBogoAddUxForAddLine,
  getBogoAddUxForIncrement,
};
